import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Filters, SortingRule } from "react-table";

export type PaginationState = {
  search?: string;
  page?: number;
  filter?: Filters<any>;
  sort?: Array<SortingRule<string>>;
};

const KNOWN_KEYS = ["sort", "direction", "search", "page"];

const urlSearchToPaginationState = (initialState: PaginationState = {}): PaginationState => {
  const paginationState: PaginationState = {
    page: 1,
    search: "",
    filter: [],
    sort: [],
    ...initialState,
  };
  const query = new URLSearchParams(window.location?.search || "");
  const page = query.get("page");
  const search = query.get("search");
  const sort = query.get("sort");
  const direction = query.get("direction");
  if (page) {
    paginationState.page = parseInt(page, 10);
    if (Number.isNaN(paginationState.page)) {
      paginationState.page = 1;
    }
  }
  if (search) {
    paginationState.search = search;
  }
  if (sort) {
    paginationState.sort = [
      {
        id: sort,
        desc: direction.toLowerCase() == "desc",
      },
    ];
  }
  let hasFilter = false;
  const newFilter = [];
  for (const [id, value] of query.entries()) {
    if (!KNOWN_KEYS.includes(id)) {
      hasFilter = true;
      newFilter.push({ id, value });
    }
  }
  if (hasFilter) {
    paginationState.filter = newFilter;
  }
  return paginationState;
};

export const paginationStateToUrlSearch = (state: PaginationState): string => {
  const urlParams: Record<string, string> = {};
  if (state.page && state.page != 1) {
    urlParams.page = state.page.toString();
  }
  if (state.search) {
    urlParams.search = state.search;
  }
  if (state.sort && state.sort.length > 0) {
    urlParams.sort = state.sort[0].id;
    urlParams.direction = state.sort[0].desc ? "DESC" : "ASC";
  }
  if (state.filter && state.filter.length > 0) {
    state.filter.forEach(({ id, value }) => {
      if (!KNOWN_KEYS.includes(id)) {
        urlParams[id] = value;
      }
    });
  }
  return new URLSearchParams(urlParams).toString();
};

export default function useStatefulPagination(
  defaultParameters: PaginationState = {},
  onLoad?: (initialPaginationState: PaginationState) => void,
) {
  const [page, setPage] = useState(defaultParameters.page ?? 1);
  const [search, setSearch] = useState(defaultParameters.search ?? "");
  const [sort, setSort] = useState(defaultParameters.sort ?? []);
  const [filter, setFilter] = useState(defaultParameters.filter ?? []);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const opts = urlSearchToPaginationState(defaultParameters);
    setPage(opts.page);
    setSearch(opts.search);
    setSort(opts.sort);
    setFilter(opts.filter);
    if (onLoad) {
      onLoad(opts);
    }
  }, []);

  useEffect(() => {
    history.replace({
      pathname: location.pathname,
      search: paginationStateToUrlSearch({ page, search, sort, filter }),
    });
  }, [page, search, sort, filter]);

  const updatePage = useCallback(
    (newPage) => {
      if (page != newPage) {
        setPage(newPage);
      }
    },
    [page, setPage],
  );

  const updateSearch = useCallback(
    (newSearch) => {
      if (search != newSearch) {
        setPage(1);
        setSearch(newSearch);
      }
    },
    [search, setSearch],
  );

  const updateSort = useCallback(
    (newSort: Array<SortingRule<string>> = []) => {
      if (
        sort.length == 0 ||
        sort.length != newSort.length ||
        sort[0].id != newSort[0].id ||
        sort[0].desc != newSort[0].desc
      ) {
        setSort(newSort);
      }
    },
    [sort, setSort],
  );

  const updateFilter = useCallback(
    (newFilter: Filters<any> = []) => {
      if (filter.length != newFilter.length) {
        setPage(1);
        setFilter(newFilter);
        return;
      }
      if (
        newFilter.some(
          ({ id, value }) => !(filter || []).some((item) => item.id == id && item.value == value),
        )
      ) {
        setPage(1);
        setFilter(newFilter);
      }
    },
    [filter, setFilter],
  );

  return {
    state: {
      page,
      search,
      sort,
      filter,
    },
    linkTo: useCallback(
      (route: string) => {
        history.push(route, {
          pagination: urlSearchToPaginationState(defaultParameters),
        });
      },
      [page, search, sort, filter],
    ),
    updatePage,
    updateSearch,
    updateSort,
    updateFilter,
  };
}
